// eslint-disable-next-line spaced-comment
/// <reference types="../../src/liburi" />
import * as URI from 'spotify-liburi';
import { EventEmitter } from '@spotify-internal/emitter';
import { logging } from '@js-sdk/debug-tools';
import { RepeatMode } from '../enums/repeat_mode';
import { HarmonyErrors } from '../enums/errors';
import { DeviceDeactivatedReason } from '../enums/device_deactivated_reason';
import { PlayerAPIClientError } from '../error/connect_service/client';
import { MetadataDecorator } from './metadata_decorator';
import { createContextTracks, QueueManager, } from './player_queue';
const debugLogger = logging.forTag('harmony.controller');
const DEFAULT_WINDOW_LENGTH = 2;
const TRACKING_INTERVAL = 500;
const EXTRACT_RESTRICTION_NAME = /^disallow_([^]+)_reasons$/;
const EXPOSED_RESTRICTIONS = {
    disallow_pausing_reasons: true,
    disallow_peeking_next_reasons: true,
    disallow_peeking_prev_reasons: true,
    disallow_resuming_reasons: true,
    disallow_seeking_reasons: true,
    disallow_skipping_next_reasons: true,
    disallow_skipping_prev_reasons: true,
    disallow_toggling_repeat_context_reasons: true,
    disallow_toggling_repeat_track_reasons: true,
    disallow_toggling_shuffle_reasons: true,
    disallow_transferring_playback_reasons: true,
};
const Licenses = {
    PREMIUM: 'premium',
    ON_DEMAND: 'on-demand',
};
export class HarmonyController extends EventEmitter {
    constructor(options) {
        super();
        this._suppressed = false;
        this._lastDeviceInfoData = null;
        this._state = null;
        this._lastPlayerState = null;
        this._isOrphanedState = false;
        this._progressPosition = 0;
        this._lastProgressTs = 0;
        this._trackingIntervalId = 0;
        /**
         * An instance of the Queue class.
         */
        this._queue = null;
        this._client = options.client;
        this._serverTime = options.serverTime;
        this._version = options.version;
        this._playerClient = options.playerClient;
        this._disableProgress = !!options.disableProgress;
        this._transport = this._client.getPublicTransport();
        this._decorate = !!options.decorateTrackWindow;
        this._metadataDecorator = new MetadataDecorator({
            transport: this._transport,
        });
        // bindings
        this._onClientBeforeDisconnect = this._onClientBeforeDisconnect.bind(this);
        this._onDeviceStateChanged = this._onDeviceStateChanged.bind(this);
        this._onPlayerStateChanged = this._onPlayerStateChanged.bind(this);
        this._onProgressTracking = this._onProgressTracking.bind(this);
        this._client.on("before_disconnect" /* CLIENT_BEFORE_DISCONNECT */, this._onClientBeforeDisconnect);
        this.proxyEmit(this._playerClient, "max_subscriptions_reached" /* CONNECT_API_MAX_SUBSCRIPTIONS_REACHED */, "max_subscriptions_reached" /* CONTROLLER_MAX_SUBSCRIPTIONS_REACHED */);
        this.proxyEmit(this._playerClient, "devices_changed" /* CONNECT_API_DEVICES_CHANGED */, "devices_changed" /* CONTROLLER_DEVICES_CHANGED */);
        this.proxyEmit(this._playerClient, "registered" /* CONNECT_API_REGISTERED */, "registered" /* CONTROLLER_REGISTERED */);
        this.proxyEmit(this._playerClient, "deregistered" /* CONNECT_API_DEREGISTERED */, "deregistered" /* CONTROLLER_DEREGISTERED */);
        this._playerClient.on("device_state_changed" /* CONNECT_API_DEVICE_STATE_CHANGED */, this._onDeviceStateChanged);
        this._playerClient.on("player_state_changed" /* CONNECT_API_PLAYER_STATE_CHANGED */, this._onPlayerStateChanged);
    }
    static create(options) {
        return new HarmonyController(options);
    }
    _onClientBeforeDisconnect(e) {
        if (e) {
            e.data.awaitPromise(this._playerClient.deregister().catch(() => {
                // Ignore any errors from deregistering that might prevent other waiters
                // from disconnecting.
            }));
        }
    }
    _onDeviceStateChanged(e) {
        var _a, _b;
        const { deviceState: deviceInfo, hasOrphanedState } = e.data;
        const wasActive = !!((_b = (_a = this._lastDeviceInfoData) === null || _a === void 0 ? void 0 : _a.deviceInfo) === null || _b === void 0 ? void 0 : _b.local);
        this._lastDeviceInfoData = {
            deviceInfo: deviceInfo,
            hasOrphanedState: hasOrphanedState,
        };
        if (wasActive && !(deviceInfo === null || deviceInfo === void 0 ? void 0 : deviceInfo.local)) {
            // This device was previously active, but not anymore. Determine the
            // possible cause of the deactivation and emit an event.
            this._playerClient
                .getDevices()
                .then(({ localDevice }) => {
                // Default reason would be stopped, as it cannot be detected through
                // other means.
                let reason = DeviceDeactivatedReason.STOPPED;
                if (!hasOrphanedState) {
                    // The current player state is not orphaned, so it's playing from
                    // somewhere else. This could be due to a transfer or because the
                    // device triggered a new playback.
                    reason = DeviceDeactivatedReason.REMOTE_ACTIVATED;
                }
                else if (!localDevice) {
                    // The device is orphaned and additionally the local device is no
                    // longer in the cluster. This means that the device was either
                    // disconnected or logged out.
                    reason = DeviceDeactivatedReason.LOCAL_PLAYER_DISABLED;
                }
                this.emit("device_deactivated" /* CONTROLLER_DEVICE_DEACTIVATED */, { reason: reason });
            })
                .catch((maybeError) => {
                debugLogger.warn('Device deactivation checking failed.', maybeError);
            });
        }
        if (!this._suppressed) {
            this.emit("device_info_changed" /* CONTROLLER_DEVICE_INFO_CHANGED */, this._lastDeviceInfoData);
        }
    }
    /**
     * Starts tracking the state of the player
     */
    _startProgressTracking() {
        this._stopProgressTracking();
        this._lastProgressTs = Date.now();
        this._onProgressTracking();
        this._trackingIntervalId = setInterval(this._onProgressTracking, TRACKING_INTERVAL);
    }
    /**
     * Stops tracking the state of the player
     */
    _stopProgressTracking() {
        if (this._trackingIntervalId) {
            clearInterval(this._trackingIntervalId);
            this._trackingIntervalId = 0;
        }
    }
    /**
     * It tracks the state of the player
     */
    _onProgressTracking() {
        const now = Date.now();
        this._progressPosition += now - this._lastProgressTs;
        this.emit("progress" /* CONTROLLER_PROGRESS */, {
            position: this._progressPosition,
            timestamp: Date.now(),
        });
        this._lastProgressTs = now;
    }
    /**
     * Used to set the license of the current context.
     *
     * Note that this is done to support dynamic devices, where the license
     * changes depending on the device that's issuing the play command.
     *
     * @return A promise containing the license value.
     */
    _getLicense() {
        return this._client
            .getProductState()
            .then((productState) => {
            // FIXME: Find a way to do this in the backend instead.
            return productState.product === 'premium'
                ? Licenses.PREMIUM
                : Licenses.ON_DEMAND;
        })
            .catch(() => {
            return Licenses.ON_DEMAND;
        });
    }
    _generatePlayOptions(options, noOverrides) {
        return this._getLicense().then((license) => {
            const preparedOptions = {
                license: license,
            };
            if (!options) {
                return preparedOptions;
            }
            if ('index' in options ||
                'trackUID' in options ||
                'trackURI' in options) {
                preparedOptions.skip_to = {
                    track_uid: options.trackUID,
                    track_index: options.index,
                    track_uri: options.trackURI,
                };
            }
            if ('initialOffset' in options) {
                preparedOptions.seek_to = options.initialOffset;
            }
            if ('paused' in options) {
                preparedOptions.initially_paused = options.paused;
            }
            if ('alwaysPlaySomething' in options) {
                preparedOptions.always_play_something = options.alwaysPlaySomething;
            }
            if (noOverrides) {
                // We do not want to process other options, so return early.
                return preparedOptions;
            }
            preparedOptions.player_options_override = {};
            const optionsOverride = preparedOptions.player_options_override;
            if ('shuffle' in options) {
                optionsOverride.shuffling_context = options.shuffle;
            }
            if ('repeatMode' in options) {
                switch (options.repeatMode) {
                    case RepeatMode.CONTEXT:
                        optionsOverride.repeating_context = true;
                        optionsOverride.repeating_track = false;
                        break;
                    case RepeatMode.TRACK:
                        optionsOverride.repeating_track = true;
                        optionsOverride.repeating_context = false;
                        break;
                    case RepeatMode.OFF:
                        optionsOverride.repeating_track = false;
                        optionsOverride.repeating_context = false;
                        break;
                    default:
                        // No-op, use defaults.
                        break;
                }
            }
            return preparedOptions;
        });
    }
    _setFilterAndSort(context, options) {
        if (options && options.filter && context.metadata) {
            context.metadata['filtering.predicate'] = options.filter;
        }
        if (options && options.sort && context.metadata) {
            context.metadata['sorting.criteria'] = options.sort;
        }
    }
    _setMetadataOptions(context, options) {
        if (!options) {
            return;
        }
        const metadata = context.metadata || {};
        if ('autoplayCandidate' in options) {
            metadata.autoplay_candidate = options.autoplayCandidate
                ? 'true'
                : 'false';
        }
        context.metadata = metadata;
    }
    _generatePlayOrigin(options) {
        // We need to send feature classes as an array (it's actually a set, but the
        // backend should dedupe it), so we need to ensure that it's an array.
        if (options &&
            options.featureClasses &&
            !Array.isArray(options.featureClasses)) {
            throw new TypeError('PlayOptions playOrigin.featureClasses must be an array of strings.');
        }
        const playOrigin = {
            feature_identifier: (options && options.featureIdentifier) || 'harmony',
            feature_version: (options && options.featureVersion) || this._version,
            feature_classes: options && options.featureClasses,
            view_uri: options && options.viewURI,
            external_referrer: options && options.externalReferrer,
            referrer_identifier: options && options.referrerIdentifier,
        };
        return playOrigin;
    }
    _generateLoggingParams(loggingParams) {
        const { pageInstanceId, interactionId } = loggingParams;
        return {
            page_instance_ids: pageInstanceId ? [pageInstanceId] : [],
            interaction_ids: interactionId ? [interactionId] : [],
        };
    }
    _parseRestrictions(state) {
        const res = {};
        for (const key in state.restrictions) {
            if (state.restrictions.hasOwnProperty(key) &&
                EXPOSED_RESTRICTIONS[key]) {
                res[key] = state.restrictions[key];
            }
            else if (EXPOSED_RESTRICTIONS[key]) {
                res[key] = [];
            }
        }
        return res;
    }
    _parseDisallows(state) {
        const res = {};
        const restrictions = state.restrictions;
        for (const key in restrictions) {
            if (restrictions.hasOwnProperty(key) &&
                EXPOSED_RESTRICTIONS[key]) {
                const restriction = restrictions[key];
                const disallow = !!(restriction && restriction.length);
                res[key.replace(EXTRACT_RESTRICTION_NAME, '$1')] = disallow;
            }
        }
        return res;
    }
    _onPlayerStateChanged(e) {
        this._isOrphanedState = e.data.orphaned;
        this._parsePlayerState(e.data.playerState).then((state) => {
            this._state = state;
            if (!this._suppressed) {
                this.emit("state_changed" /* CONTROLLER_STATE_CHANGED */, {
                    state: state ? Object.assign({}, state) : null,
                    orphaned: e.data.orphaned,
                });
            }
            // Progress tracking
            if (!this._disableProgress && state) {
                this._progressPosition = state.position;
                if (state.paused) {
                    this._stopProgressTracking();
                }
                else {
                    this._startProgressTracking();
                }
            }
        });
    }
    _parsePlayerState(state) {
        this._lastPlayerState = state;
        if (!state) {
            return Promise.resolve(null);
        }
        return this._getTrackWindow(state).then((trackWindow) => {
            const formatted = {
                context: {
                    uri: state.context_uri || null,
                    metadata: state.context_metadata || {},
                },
                timestamp: state.timestamp,
                duration: state.duration ||
                    (trackWindow.current_track &&
                        'uri' in trackWindow.current_track &&
                        trackWindow.current_track.duration_ms) ||
                    0,
                position: !!state.is_paused
                    ? state.position_as_of_timestamp
                    : this._offsetPosition(state.timestamp, state.position_as_of_timestamp),
                paused: !!state.is_paused,
                shuffle: !!(state.options && state.options.shuffling_context),
                repeat_mode: this._getStateRepeatMode(state),
                restrictions: this._parseRestrictions(state),
                disallows: this._parseDisallows(state),
                track_window: trackWindow,
                bitrate: null,
            };
            return formatted;
        });
    }
    _offsetPosition(timestamp, position, optNow) {
        const currentTime = optNow || this._serverTime.getApproximate();
        const offset = currentTime - timestamp;
        const maybePosition = position + offset;
        return maybePosition < 0 ? 0 : maybePosition;
    }
    _isRestricted(state, restriction) {
        if (!state || !state.restrictions) {
            return false;
        }
        const restrictionName = `disallow_${restriction}_reasons`;
        return !!(state.restrictions[restrictionName] || []).length;
    }
    _getTrackWindow(state) {
        const trackWindow = {
            current_track: null,
            next_tracks: [],
            previous_tracks: [],
        };
        const map = {};
        const trackList = [];
        if (!state.track) {
            return Promise.resolve(trackWindow);
        }
        // parse current track
        const currentURI = state.track.uri;
        if (!currentURI) {
            return Promise.resolve(trackWindow);
        }
        const metadataDecorator = this._metadataDecorator;
        const uriObj = URI.from(currentURI);
        const maybeLocal = metadataDecorator.formatLocalTrack(uriObj);
        const maybeAd = metadataDecorator.formatAd(uriObj, state.track);
        trackWindow.current_track = maybeLocal ||
            maybeAd || {
            uri: currentURI,
            type: uriObj && uriObj.type ? uriObj.type : 'unknown',
        };
        if (!maybeLocal && !maybeAd) {
            map[currentURI] = 1;
            trackList.push(currentURI);
        }
        if (!this._isRestricted(state, 'peeking_next')) {
            this._fillWindow(trackList, trackWindow.next_tracks, map, state.next_tracks ? state.next_tracks.slice(0, 5) : []);
        }
        if (!this._isRestricted(state, 'peeking_prev')) {
            this._fillWindow(trackList, trackWindow.previous_tracks, map, state.prev_tracks ? state.prev_tracks.slice(-5) : [], true);
        }
        return this._decorate
            ? this._metadataDecorator.decorateTrackWindow(trackWindow, trackList)
            : Promise.resolve(trackWindow);
    }
    _fillWindow(list, win, map, tracks, rv) {
        if (rv) {
            tracks.reverse();
        }
        for (let i = 0, l = tracks.length; i < l; i++) {
            const uri = tracks[i].uri;
            const uriObj = URI.from(uri);
            if (!uri ||
                uri === 'spotify:delimiter' ||
                (uriObj &&
                    (uriObj.type === URI.Type.AD ||
                        uriObj.type === URI.Type.INTERRUPTION))) {
                // Disallow non-URIs and ads from appearing in the up next queue.
                continue;
            }
            const maybeLocal = this._metadataDecorator.formatLocalTrack(uriObj);
            const track = maybeLocal || {
                uri: uri,
                type: 'unknown',
            };
            if (!maybeLocal && !(uri in map)) {
                map[uri] = 1;
                list.push(uri);
            }
            if (rv) {
                win.unshift(track);
            }
            else {
                win.push(track);
            }
            if (win.length === DEFAULT_WINDOW_LENGTH) {
                break;
            }
        }
    }
    _getStateRepeatMode(state) {
        const options = state.options;
        if (options && options.repeating_track) {
            return RepeatMode.TRACK;
        }
        if (options && options.repeating_context) {
            return RepeatMode.CONTEXT;
        }
        return RepeatMode.OFF;
    }
    _play(context, deviceId, options) {
        this._setFilterAndSort(context, options);
        this._setMetadataOptions(context, options);
        return this._generatePlayOptions(options).then((preparedOptions) => {
            const payload = {
                context,
                play_origin: this._generatePlayOrigin(options && options.playOrigin),
                options: preparedOptions,
            };
            if (options === null || options === void 0 ? void 0 : options.loggingParams) {
                payload.logging_params = this._generateLoggingParams(options.loggingParams);
            }
            return this._playerClient.play(payload, deviceId);
        });
    }
    _prepareTracks(maybeTracks, options) {
        const tracks = [];
        const VALID_TRACK_TYPES = [URI.Type.TRACK, URI.Type.EPISODE];
        for (let i = 0, l = maybeTracks.length; i < l; i++) {
            let track = maybeTracks[i];
            if (!track) {
                return Promise.reject(new TypeError('Null value inside track array.'));
            }
            let uri;
            if (typeof track === 'string') {
                uri = URI.from(track);
                track = {
                    uri: track,
                };
            }
            else {
                uri = URI.from(track.uri);
            }
            if (!uri) {
                return Promise.reject(new TypeError(`Invalid uri string: ${uri}`));
            }
            if (VALID_TRACK_TYPES.indexOf(uri.type) === -1) {
                return Promise.reject(new TypeError(`Invalid track uri: ${track.uri}`));
            }
            if (!track.metadata) {
                track.metadata = {};
            }
            if (options && options.queued) {
                track.metadata.is_queued = 'true';
            }
            tracks.push(track);
        }
        return Promise.resolve(tracks);
    }
    getActiveDevice() {
        return this._playerClient.getActiveDevice();
    }
    getCurrentState(noOrphans) {
        if (noOrphans && this._isOrphanedState) {
            return Promise.resolve(null);
        }
        const currentState = this._state;
        if (currentState) {
            // Offset the time
            if (!currentState.paused) {
                const now = this._serverTime.getApproximate();
                currentState.position = this._offsetPosition(currentState.timestamp, currentState.position, now);
                // As position is updated, the timestamp also needs to be updated
                // so the offset keeps working correctly on following calls on the same
                // state
                currentState.timestamp = now;
            }
        }
        return Promise.resolve(currentState ? Object.assign({}, currentState) : null);
    }
    getContextPlayerState() {
        debugLogger.warn('The `getContextPlayerState()` method is not well supported. ' +
            'Please avoid using it directly.');
        return this._playerClient.getContextPlayerState();
    }
    getDevices() {
        return this._playerClient.getDevices();
    }
    playURI(uri, deviceId, options) {
        if (!URI.from(uri)) {
            return Promise.reject(new TypeError('Invalid Spotify URI'));
        }
        const context = {
            uri: uri,
            url: `context://${uri}`,
            metadata: (options && options.contextMetadata) || {},
        };
        return this._play(context, deviceId, options);
    }
    playTracks(items, deviceId, options) {
        if (!Array.isArray(items)) {
            return Promise.reject(new TypeError('Invalid tracks array.'));
        }
        return this._prepareTracks(items).then((tracks) => {
            const context = {
                uri: options && typeof options.contextURI !== 'undefined'
                    ? options.contextURI
                    : 'spotify:app:playerapi-client',
                metadata: (options && options.contextMetadata) || {},
                pages: [
                    {
                        tracks: tracks,
                    },
                ],
            };
            return this._play(context, deviceId, options);
        });
    }
    updateCurrentContext(deviceId, options = {}) {
        return this._playerClient.updateCurrentContext(deviceId, options);
    }
    setQueue(nextTracks, previousTracks, deviceId) {
        if (!Array.isArray(nextTracks)) {
            return Promise.reject(new TypeError('Invalid nextTracks array.'));
        }
        if (!Array.isArray(previousTracks)) {
            return Promise.reject(new TypeError('Invalid previousTracks array.'));
        }
        return Promise.all([
            this._prepareTracks(nextTracks),
            this._prepareTracks(previousTracks),
        ]).then(([next, previous]) => {
            const payload = {
                next_tracks: next.length ? next : undefined,
                prev_tracks: previous.length ? previous : undefined,
            };
            return this._playerClient.setQueue(payload, deviceId);
        });
    }
    togglePlay(deviceId) {
        return this._playerClient.togglePlay(deviceId);
    }
    transfer(deviceId, options) {
        return this._generatePlayOptions(options, true).then((preparedOptions) => {
            return this._playerClient.transfer(deviceId, preparedOptions);
        });
    }
    pause(deviceId) {
        return this._playerClient.pause(deviceId);
    }
    resume(deviceId) {
        return this._playerClient.resume(deviceId);
    }
    nextTrack(deviceId, options) {
        const preparedOptions = {};
        if (options === null || options === void 0 ? void 0 : options.track) {
            preparedOptions.track = createContextTracks([options.track])[0];
        }
        return this._playerClient.nextTrack(deviceId, preparedOptions);
    }
    previousTrack(deviceId, options) {
        const preparedOptions = {};
        if (options === null || options === void 0 ? void 0 : options.track) {
            preparedOptions.track = createContextTracks([options.track])[0];
        }
        return this._playerClient.previousTrack(deviceId, preparedOptions);
    }
    seek(position, deviceId) {
        return this._playerClient.seek(position, deviceId);
    }
    setShuffle(shuffle, deviceId) {
        return this._playerClient.setShuffle(shuffle, deviceId);
    }
    toggleShuffle(deviceId) {
        return this._playerClient.toggleShuffle(deviceId);
    }
    setVolume(volume, deviceId) {
        return this._playerClient.setVolume(volume, deviceId);
    }
    logout(deviceId) {
        return this._playerClient.logout(deviceId);
    }
    setRepeatMode(repeatMode, deviceId) {
        return this.getCurrentState().then((state) => {
            if (!state) {
                return Promise.reject(new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_CLIENT_NO_STATE, 'Nothing playing'));
            }
            const playerClient = this._playerClient;
            const disallows = state.disallows;
            switch (repeatMode) {
                case RepeatMode.OFF:
                    if (disallows.toggling_repeat_context &&
                        disallows.toggling_repeat_track) {
                        // We technically cannot set anything, as all operations are
                        // forbidden. We are most likely already not repeated, so just
                        // return.
                        return false;
                    }
                    return playerClient.setOptions({
                        repeating_context: disallows.toggling_repeat_context
                            ? undefined
                            : false,
                        repeating_track: disallows.toggling_repeat_track
                            ? undefined
                            : false,
                    }, deviceId);
                case RepeatMode.CONTEXT:
                    if (disallows.toggling_repeat_context) {
                        // Ideally we want to throw an error here, but that would be a
                        // breaking API change, so we just return false.
                        return false;
                    }
                    return playerClient.setOptions({
                        repeating_context: true,
                        repeating_track: disallows.toggling_repeat_track
                            ? undefined
                            : false,
                    }, deviceId);
                case RepeatMode.TRACK:
                    if (disallows.toggling_repeat_track) {
                        // Ideally we want to throw an error here, but that would be a
                        // breaking API change, so we just return false.
                        return false;
                    }
                    return playerClient.setOptions({
                        repeating_context: disallows.toggling_repeat_context
                            ? undefined
                            : true,
                        repeating_track: true,
                    }, deviceId);
                default:
                    return Promise.reject(new TypeError('Unknown repeat mode.'));
            }
        });
    }
    cycleRepeatMode(deviceId) {
        return this.getCurrentState().then((state) => {
            if (!state) {
                return Promise.reject(new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_CLIENT_NO_STATE, 'Nothing playing'));
            }
            let nextRepeatMode = null;
            // The repeat mode cycle goes from none to context to track and then back
            // to none. However, restrictions can disable cycling from one to the
            // other, so we need to skip those cases and go on to the next part of
            // the cycle.
            const disallows = state.disallows;
            switch (state.repeat_mode) {
                case RepeatMode.OFF:
                    if (!disallows.toggling_repeat_context) {
                        nextRepeatMode = RepeatMode.CONTEXT;
                    }
                    else if (!disallows.toggling_repeat_track) {
                        nextRepeatMode = RepeatMode.TRACK;
                    }
                    else {
                        // Repeat is disabled, so we simply return.
                        return Promise.resolve(false);
                    }
                    break;
                case RepeatMode.CONTEXT:
                    if (!disallows.toggling_repeat_track) {
                        nextRepeatMode = RepeatMode.TRACK;
                    }
                    else {
                        nextRepeatMode = RepeatMode.OFF;
                    }
                    break;
                case RepeatMode.TRACK:
                    nextRepeatMode = RepeatMode.OFF;
                    break;
                default:
                    nextRepeatMode = RepeatMode.OFF;
            }
            return this.setRepeatMode(nextRepeatMode, deviceId);
        });
    }
    register() {
        return this._playerClient.register();
    }
    suppressEvents() {
        this._suppressed = true;
    }
    unsuppressEvents() {
        this._suppressed = false;
        if (this._lastDeviceInfoData) {
            this.emit("device_info_changed" /* CONTROLLER_DEVICE_INFO_CHANGED */, this._lastDeviceInfoData);
        }
        if (this._state) {
            this.emit("state_changed" /* CONTROLLER_STATE_CHANGED */, {
                state: this._state,
            });
        }
    }
    getQueueManager(runner, options = {}) {
        if (!this._queue) {
            const initialContextPlayerState = options.reportInitial
                ? this._lastPlayerState
                : null;
            this._queue = new QueueManager(Object.assign({ connectClient: this._playerClient, initialContextPlayerState,
                runner }, options));
        }
        return this._queue;
    }
}
//# sourceMappingURL=controller.js.map